import type {HydratedAPITask} from '../types/api-task'
import type {Session} from '../types/session'
import type {UITask} from '../types/task'

// Copilot CLI App IDs. Temporarily hardcoded here until we have a more robust way to identify CLI sessions/tasks.
// Production ID from Mission Control providers.go
const CLI_AGENT_ID_PROD = 1693627

// Dev environment ID for local testing (only checked in development mode)
const CLI_AGENT_ID_DEV = 841

/**
 * Checks if an agent ID belongs to the Copilot CLI.
 * In development mode, also checks for the dev environment ID.
 */
export function isCLIAgentId(agentId: number | string | undefined): boolean {
  if (agentId === undefined) return false

  const numericId = typeof agentId === 'string' ? parseInt(agentId, 10) : agentId

  if (numericId === CLI_AGENT_ID_PROD) return true
  if (process.env.NODE_ENV === 'development' && numericId === CLI_AGENT_ID_DEV) return true

  return false
}

/**
 * Checks if a session was created by the Copilot CLI.
 */
export function isCLISession(session: Session | null): boolean {
  if (!session) return false
  return isCLIAgentId(session.agent_id)
}

/**
 * Checks if a task was created by the Copilot CLI.
 * Uses the agentId field which contains the first collaborator's agent_id.
 */
export function isTaskFromCLI(task: UITask | null | undefined): boolean {
  if (!task) return false
  return isCLIAgentId(task.agentId)
}

/**
 * Checks if an API task was created by the Copilot CLI.
 * Uses the first agent_collaborator's agent_id.
 */
export function isAPITaskFromCLI(task: HydratedAPITask | null | undefined): boolean {
  if (!task) return false
  return task.agent_collaborators?.some(c => isCLIAgentId(c.agent_id)) ?? false
}
